'use client'

import getLocale from '@/stores/locales'
import PhIcon from '@mercadoni/sinister/dist/cjs/PhIcon'
import { device } from '@mercadoni/sinister/dist/cjs/shared/utils'
import { Button, Col, CollapseProps, Row, Typography } from 'antd'
import { useEffect, useState } from 'react'
import useWindowMobile from '../useCases/useWindowMobile'
import './SortFilterMobileCol.css'

import goToSearchV2 from '@/navigation/CSR/goToSearchV2/goToSearch'
import { useRouter } from 'next/navigation'
import {
  AggregatesValue,
  IParamsByUrl,
  SortFilterMobileColProps,
} from '~types/search'
import getActiveAggregates from '../useCases/getActiveAggregates'
import Content from './CollapsableAggregates/Content'
import Label from './CollapsableAggregates/Label'
import DrawerFilter from './DrawerFilter'
import DrawerSort from './DrawerSort'

const { Text } = Typography

const SortFilterMobileCol = ({
  userAgent,
  numberOfFiltersApplied,
  clientConfig,
  selectedSort,
  aggregates,
  currencyLocale,
  currencyFormat,
  urlParams,
}: SortFilterMobileColProps) => {
  const router = useRouter()
  const buttonSize = 'large'
  const copy = getLocale()

  const selectedAggregates = new Map<string, AggregatesValue>()

  useEffect(() => {
    aggregates.forEach((aggregate) => {
      selectedAggregates.set(aggregate.name, aggregate.value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregates])

  const handleValueListener = (filter: string, value: AggregatesValue) => {
    selectedAggregates.set(filter, value)
  }

  const isScreenMobile = useWindowMobile()
  const isMobile = device(userAgent).isMobile()

  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const activeAggregates = getActiveAggregates({
    aggregates,
    currencyLocale,
    currencyFormat,
    valueListener: handleValueListener,
  })
  const items: CollapseProps['items'] = activeAggregates.map((aggregate) => ({
    key: aggregate.key,
    label: <Label aggregateName={aggregate.label} />,
    children: (
      <Content
        aggregate={aggregate.contentProps.aggregate}
        value={aggregate.contentProps.value}
        valueListener={aggregate.contentProps.valueListener}
        currencyLocale={aggregate.contentProps.currencyLocale}
        currencyFormat={aggregate.contentProps.currencyFormat}
      />
    ),
  }))

  const openSortDrawer = () => {
    setIsSortDrawerOpen(true)
  }

  const openFilterDrawer = () => {
    setIsFilterDrawerOpen(true)
  }

  const onClearAll = async () => {
    await goToSearchV2({ router, urlParams, action: 'clearAll' })
  }
  const onApply = async () => {
    const newParams = {
      ...urlParams,
      ...Object.fromEntries(selectedAggregates),
    } as IParamsByUrl
    await goToSearchV2({
      router,
      urlParams: newParams,
      action: 'applyFilters',
    })
  }
  const sortSelected = async (value: string) => {
    await goToSearchV2({
      router,
      urlParams: urlParams,
      action: 'sortSelected',
      value,
    })
  }

  if (isMobile || isScreenMobile) {
    return (
      <Col span={24}>
        <Row className="search-filter-sort-mobile">
          <Col span={12}>
            <Button
              color="default"
              variant="text"
              size={buttonSize}
              block
              onClick={openSortDrawer}
            >
              <PhIcon size={20} className="ph-arrows-left-right red-color" />
              <Text strong> {copy?.filter?.order}</Text>
            </Button>
          </Col>
          <Col span={12}>
            <Button
              color="default"
              variant="text"
              size={buttonSize}
              block
              onClick={openFilterDrawer}
            >
              <PhIcon size={20} className="ph-funnel red-color" />
              <Row>
                <Col span={24}>
                  <Text strong> {copy?.filter?.filterBy}</Text>
                </Col>
                <Col span={24}>
                  {numberOfFiltersApplied > 0 && (
                    <Text style={{ fontSize: 10 }}>
                      {`${numberOfFiltersApplied} ${copy?.filter?.drawerTitle}`}
                    </Text>
                  )}
                </Col>
              </Row>
            </Button>
          </Col>
        </Row>
        <DrawerSort
          title={copy?.filter?.order}
          copyFilter={copy?.filter}
          isSortDrawerOpen={isSortDrawerOpen}
          setIsSortDrawerOpen={setIsSortDrawerOpen}
          sortOptionsConfig={clientConfig?.sortOptions || []}
          selectedSort={selectedSort}
          sortSelected={sortSelected}
        />
        <DrawerFilter
          isFilterDrawerOpen={isFilterDrawerOpen}
          setIsFilterDrawerOpen={setIsFilterDrawerOpen}
          onClearAll={onClearAll}
          onApply={onApply}
          items={items}
          buttonSize={buttonSize}
          title={copy?.filter?.filterBy}
          applyTitle={copy?.filter?.apply}
          clearTitle={copy?.filter?.clear}
        />
      </Col>
    )
  }

  return null
}

export default SortFilterMobileCol
